// csai-web entry point. Mounts <App/> into #root with React Router.
import "@csai/design-system/tokens.css";
import "./styles/global.css";
import "./styles/primitives.css";
import "./styles/ds-primitives.css";
import "./styles/login.css";
import "./styles/shell.css";
import "./styles/home.css";
import "./styles/templates.css";
import "./styles/doc-frame.css";
import "./styles/index-health.css";
import "./styles/activity.css";
import "./styles/reattestation.css";
import "./styles/attestation.css";
import "./styles/tenants.css";
import "./styles/settings.css";
import "./styles/upload.css";
import "./styles/atlas.css";
import "./styles/classification.css";
import "./styles/ingestion.css";
import "./styles/analytics.css";
import "./styles/ba-home.css";
import "./styles/perks-register.css";
import "./styles/drafts.css";
import "./styles/precedents.css";
import "./styles/studio-agents.css";
import "./styles/mcp-consent.css";
import "./styles/persona-admin.css";
import "./styles/marketplace.css";
import "./styles/support.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App";
import { AuthProvider } from "./auth/AuthProvider";
import { selectAuthAdapter } from "./auth/selectAuthAdapter";
import { createQueryClient } from "./data/queryClient";
import {
  EMPTY_FLAGS,
  FeatureProvider,
  type FlagSnapshot,
} from "./features/FeatureContext";
import { ToastProvider, TooltipProvider } from "./components";

const queryClient = createQueryClient();

// Phase 6a foundation: feature-flag bootstrap. Production reads these
// from the auth/session response; the dev mock turns nothing on.
const flagBootstrap: FlagSnapshot = EMPTY_FLAGS;

// Auth adapter is selected at boot — VITE_AUTH_ADAPTER=firebase loads
// the FirebaseAuthAdapter (lazy chunk), otherwise MockAuthAdapter.
//
// CRITICAL: do NOT `await selectAuthAdapter()` at the MODULE top level.
// A top-level await here makes this entry module "async" in the ESM
// graph; the dynamically-imported Firebase chunks back-import from this
// entry chunk, so their evaluation blocks on this module's top-level
// await while this await blocks on importing them — a deadlock. In the
// Vite production bundle the symptom is a permanently blank #root with
// NO console error and NO network calls (works fine under the dev
// server, which doesn't bundle). Running the async work inside an
// explicit bootstrap() lets module evaluation finish synchronously,
// breaking the cycle. The .catch() also guarantees a boot failure shows
// an error on screen instead of a silent white page.
async function bootstrap(): Promise<void> {
  const root = document.getElementById("root");
  if (root === null) {
    throw new Error("Missing #root mount point in index.html");
  }

  const auth = await selectAuthAdapter();

  createRoot(root).render(
    <StrictMode>
      <AuthProvider
        auth={auth}
        onSignOut={() => {
          // S-203 — flush every cached query on sign-out so user A's
          // templates / attestations / tenants never appear in user B's
          // session.
          queryClient.clear();
        }}
      >
        <QueryClientProvider client={queryClient}>
          <FeatureProvider value={flagBootstrap}>
            <TooltipProvider delayDuration={300}>
              <ToastProvider>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </ToastProvider>
            </TooltipProvider>
          </FeatureProvider>
        </QueryClientProvider>
      </AuthProvider>
    </StrictMode>
  );
}

bootstrap().catch((err: unknown) => {
  // Never leave a silent blank page: surface the boot failure so it's
  // diagnosable in prod without a debugger.
  const message = err instanceof Error ? err.message : String(err);
  console.error("csai-web failed to start:", err);
  const mount = document.getElementById("root");
  if (mount !== null) {
    mount.textContent = `Failed to start: ${message}`;
  }
});
